import { useIntl } from 'react-intl';
import { generatePath } from 'react-router';

import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { getImageSrc } from '@/shared/lib';

import { getReportPath, useNavigateToReport } from '@/entities/reports';

import { type StructuredReportsAndStudiesType } from '@/features/reportCards';

import {
  reportCardTitle,
  TIMELINE_ICON_BY_REPORT_TYPE,
} from '@/widgets/ReportCard';

import {
  FALLBACK_SLIDE_REPORT_TYPES,
  PANORAMA_SLIDE_REPORT_TYPES,
  type ReportPreviewSlide,
} from '../config';

// Relies on the source list already being sorted newest first.
export const useReportPreviewSlides = (
  reports: StructuredReportsAndStudiesType[],
): ReportPreviewSlide[] => {
  const { formatMessage, formatDate } = useIntl();
  const navigateToReport = useNavigateToReport();

  const toSlides = (reportTypes: ReportType[]) =>
    reports.flatMap((item) => {
      const { report, reportType, date } = item;

      if (
        !report?.DisplayAssetID ||
        !report.Status?.Completed ||
        !reportTypes.includes(reportType)
      ) {
        return [];
      }

      const { ID: reportID, PatientID: patientID } = report;
      const reportPath = generatePath(getReportPath(reportType), {
        patientID,
        reportID,
      });

      return [
        {
          id: reportID,
          reportType,
          studyID: report.SourceStudyIDs[0] ?? '',
          displayAssetID: report.DisplayAssetID,
          title: formatMessage(reportCardTitle[reportType]),
          date: date ? formatDate(date, { dateStyle: 'medium' }) : '',
          icon: TIMELINE_ICON_BY_REPORT_TYPE[reportType],
          previewSrc: getImageSrc(report.DisplayAssetID, 'preview'),
          shouldContainPreview:
            reportType === ReportType.ReportType_IOXRay_GP ||
            report.Specific.case === 'DataPanoBitewings',
          open: () =>
            navigateToReport({
              reportID,
              reportType,
              patientID,
              CBCTOrthoPDFUrl: '',
            }),
          openInNewTab: () => window.open(reportPath, '_blank'),
        },
      ];
    });

  const panoramaSlides = toSlides(PANORAMA_SLIDE_REPORT_TYPES);

  return panoramaSlides.length > 0
    ? panoramaSlides
    : toSlides(FALLBACK_SLIDE_REPORT_TYPES);
};
